import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Calendar, Video, Users, XCircle, Download, Clock, MapPin, Trophy, Target, TrendingUp, Sparkles } from 'lucide-react';
import { bookingsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useThumbnails } from '../hooks/useThumbnails';
import toast from 'react-hot-toast';
import { format, parseISO, isPast, isToday, isFuture, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';

export default function CustomerClassesPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('upcoming'); // 'upcoming', 'past'

  // Fetch user's bookings
  const { data: bookingsData, isLoading } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false,
    refetchOnWindowFocus: true,
  });

  const bookings = bookingsData?.data || [];

  // Get thumbnails for booked classes (cached globally)
  const videoIds = useMemo(() =>
    [...new Set(bookings.map(b => b.schedule?.videoId).filter(Boolean))],
    [bookings]
  );
  const thumbnailsMap = useThumbnails(videoIds);

  // Calculate metrics for past classes
  const pastClassesMetrics = useMemo(() => {
    const pastBookings = bookings.filter(booking => {
      if (!booking.schedule?.specificDate) return false;
      const classDate = parseISO(booking.schedule.specificDate);
      return isPast(classDate) && !isToday(classDate);
    });

    const totalPastClasses = pastBookings.length;
    
    // Classes this month
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    const thisMonthClasses = pastBookings.filter(booking => {
      const classDate = parseISO(booking.schedule.specificDate);
      return classDate >= monthStart && classDate <= monthEnd;
    }).length;

    // Classes this year
    const yearStart = startOfYear(now);
    const yearEnd = endOfYear(now);
    const thisYearClasses = pastBookings.filter(booking => {
      const classDate = parseISO(booking.schedule.specificDate);
      return classDate >= yearStart && classDate <= yearEnd;
    }).length;

    // Streak calculation (consecutive months with at least one class)
    const classesByMonth = {};
    pastBookings.forEach(booking => {
      const classDate = parseISO(booking.schedule.specificDate);
      const monthKey = format(classDate, 'yyyy-MM');
      classesByMonth[monthKey] = (classesByMonth[monthKey] || 0) + 1;
    });
    
    const sortedMonths = Object.keys(classesByMonth).sort().reverse();
    let currentStreak = 0;
    let checkMonth = format(now, 'yyyy-MM');
    for (let i = 0; i < sortedMonths.length; i++) {
      if (sortedMonths[i] === checkMonth || 
          (i > 0 && sortedMonths[i] === format(new Date(parseISO(sortedMonths[i-1] + '-01')).setMonth(new Date(parseISO(sortedMonths[i-1] + '-01')).getMonth() - 1), 'yyyy-MM'))) {
        currentStreak++;
        const prevMonth = new Date(parseISO(checkMonth + '-01'));
        prevMonth.setMonth(prevMonth.getMonth() - 1);
        checkMonth = format(prevMonth, 'yyyy-MM');
      } else {
        break;
      }
    }

    // Motivational messages based on progress
    const getMotivationalMessage = () => {
      if (totalPastClasses === 0) {
        return {
          message: "Start your fitness journey today! Every class is a step toward your goals.",
          emoji: "🌟"
        };
      } else if (totalPastClasses < 5) {
        return {
          message: "Great start! You're building momentum. Keep going!",
          emoji: "💪"
        };
      } else if (totalPastClasses < 10) {
        return {
          message: "You're on fire! Consistency is key to success.",
          emoji: "🔥"
        };
      } else if (totalPastClasses < 25) {
        return {
          message: "Amazing progress! You're becoming a regular!",
          emoji: "⭐"
        };
      } else if (totalPastClasses < 50) {
        return {
          message: "Incredible dedication! You're a true fitness enthusiast!",
          emoji: "🏆"
        };
      } else {
        return {
          message: "You're a fitness legend! Your commitment is inspiring!",
          emoji: "👑"
        };
      }
    };

    const motivational = getMotivationalMessage();

    return {
      totalPastClasses,
      thisMonthClasses,
      thisYearClasses,
      currentStreak,
      motivational
    };
  }, [bookings]);

  // Cancel booking mutation
  const cancelBookingMutation = useMutation({
    mutationFn: (data) => bookingsAPI.cancelBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-bookings']);
      toast.success('Booking cancelled');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel booking');
    }
  });

  const handleCancelBooking = (bookingId) => {
    if (window.confirm('Are you sure you want to cancel this class booking?')) {
      cancelBookingMutation.mutate({
        bookingId,
        studentId: user.id
      });
    }
  };

  // Filter bookings
  const filteredBookings = bookings.filter(booking => {
    if (!booking.schedule) return false;
    
    const schedule = booking.schedule;
    let classDate;
    
    if (schedule.specificDate) {
      classDate = parseISO(schedule.specificDate);
    } else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
      // For recurring classes, show them as upcoming
      return filter === 'upcoming';
    } else {
      return false;
    }

    if (filter === 'upcoming') return isFuture(classDate) || isToday(classDate);
    if (filter === 'past') return isPast(classDate) && !isToday(classDate);
    
    return false;
  });

  // Sort bookings: upcoming first, then by date
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const scheduleA = a.schedule;
    const scheduleB = b.schedule;
    
    let dateA, dateB;
    
    if (scheduleA.specificDate) {
      dateA = parseISO(scheduleA.specificDate);
    } else {
      dateA = new Date(0); // Put recurring classes at the end
    }
    
    if (scheduleB.specificDate) {
      dateB = parseISO(scheduleB.specificDate);
    } else {
      dateB = new Date(0);
    }
    
    // Sort by date (ascending for upcoming, descending for past)
    if (filter === 'past') {
      return dateB - dateA;
    }
    return dateA - dateB;
  });

  // Generate iCal file for a single booking
  const generateCalendarFileForBooking = (booking) => {
    const schedule = booking.schedule;
    if (!schedule?.specificDate) {
      toast.error('This is a recurring class. Only specific date classes can be exported.');
      return;
    }

    const video = schedule?.video || {};
    const classDate = parseISO(schedule.specificDate);
    const startTime = schedule.startTime || '09:00:00';
    const [hours, minutes] = startTime.split(':');
    const startDateTime = new Date(classDate);
    startDateTime.setHours(parseInt(hours), parseInt(minutes), 0);
    
    const duration = video.duration || 3600; // Default 1 hour in seconds
    const endDateTime = new Date(startDateTime.getTime() + duration * 1000);
    
    const formatDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const title = video.title || 'Class';
    const description = video.description || '';
    const location = schedule.location || '';
    
    const event = `BEGIN:VEVENT
DTSTART:${formatDate(startDateTime)}
DTEND:${formatDate(endDateTime)}
SUMMARY:${title}
DESCRIPTION:${description.replace(/\n/g, '\\n')}
LOCATION:${location}
UID:${booking.id}@mystudioclass
END:VEVENT`;
    
    const icalContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//MyStudioClass//Customer Classes//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
${event}
END:VCALENDAR`;
    
    // Create blob and download
    const blob = new Blob([icalContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const safeTitle = (video.title || 'class').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    link.setAttribute('download', `${safeTitle}-${format(classDate, 'yyyy-MM-dd')}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Class exported to calendar!');
  };

  // Get class date and time info
  const getClassDateTime = (schedule) => {
    if (schedule.specificDate) {
      const classDate = parseISO(schedule.specificDate);
      const startTime = schedule.startTime || '09:00:00';
      const [hours, minutes] = startTime.split(':');
      return {
        date: classDate,
        time: `${hours}:${minutes}`,
        formattedDate: format(classDate, 'EEEE, MMMM d, yyyy'),
        formattedTime: `${parseInt(hours) % 12 || 12}:${minutes.padStart(2, '0')} ${parseInt(hours) >= 12 ? 'PM' : 'AM'}`,
        isPast: isPast(classDate) && !isToday(classDate),
        isToday: isToday(classDate),
        isUpcoming: isFuture(classDate) || isToday(classDate)
      };
    } else if (schedule.dayOfWeek !== null && schedule.dayOfWeek !== undefined) {
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const startTime = schedule.startTime || '09:00:00';
      const [hours, minutes] = startTime.split(':');
      return {
        date: null,
        time: `${hours}:${minutes}`,
        formattedDate: `Every ${dayNames[schedule.dayOfWeek]}`,
        formattedTime: `${parseInt(hours) % 12 || 12}:${minutes.padStart(2, '0')} ${parseInt(hours) >= 12 ? 'PM' : 'AM'}`,
        isPast: false,
        isToday: false,
        isUpcoming: true,
        isRecurring: true
      };
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)]">Your Classes</h1>
          <p className="text-[var(--text-secondary)] mt-2">View and manage your booked classes</p>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 border-b border-[var(--border-subtle)]">
        <button
          onClick={() => setFilter('upcoming')}
          className={`px-4 py-2 font-medium transition-colors ${
            filter === 'upcoming'
              ? 'text-[var(--text-secondary)] border-b-2 border-primary-600'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          Upcoming ({bookings.filter(b => {
            if (!b.schedule?.specificDate) return true; // Include recurring
            const date = parseISO(b.schedule.specificDate);
            return isFuture(date) || isToday(date);
          }).length})
        </button>
        <button
          onClick={() => setFilter('past')}
          className={`px-4 py-2 font-medium transition-colors ${
            filter === 'past'
              ? 'text-[var(--text-secondary)] border-b-2 border-primary-600'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
        >
          Past Classes ({bookings.filter(b => {
            if (!b.schedule?.specificDate) return false;
            const date = parseISO(b.schedule.specificDate);
            return isPast(date) && !isToday(date);
          }).length})
        </button>
      </div>

      {/* Metrics Section - Only show for Past Classes */}
      {filter === 'past' && pastClassesMetrics.totalPastClasses > 0 && (
        <div className="card bg-gradient-to-br from-primary-50 to-accent-50 border-2 border-[var(--border-subtle)] hover:shadow-lg transition-all duration-300">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-8 h-8 text-[var(--text-secondary)]" />
            </div>
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
                Your Progress {pastClassesMetrics.motivational.emoji}
              </h3>
              <p className="text-[var(--text-secondary)] text-lg font-medium">
                {pastClassesMetrics.motivational.message}
              </p>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="rounded-lg p-4 border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-[var(--text-secondary)]" />
                </div>
                <div>
                  <p className="text-xs text-[var(--text-secondary)] font-medium">Total Classes</p>
                  <p className="text-2xl font-bold text-[var(--text-primary)]">{pastClassesMetrics.totalPastClasses}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg p-4 border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[var(--text-secondary)] font-medium">This Month</p>
                  <p className="text-2xl font-bold text-[var(--text-primary)]">{pastClassesMetrics.thisMonthClasses}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg p-4 border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-accent-600" />
                </div>
                <div>
                  <p className="text-xs text-[var(--text-secondary)] font-medium">This Year</p>
                  <p className="text-2xl font-bold text-[var(--text-primary)]">{pastClassesMetrics.thisYearClasses}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg p-4 border border-[var(--border-subtle)]" style={{ background: 'var(--bg-elevated)' }}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-[var(--text-secondary)] font-medium">Month Streak</p>
                  <p className="text-2xl font-bold text-[var(--text-primary)]">{pastClassesMetrics.currentStreak}</p>
                  <p className="text-xs text-[var(--text-muted)] mt-1">months</p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6 pt-6 border-t border-[var(--border-subtle)]">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[var(--text-secondary)]">Your Fitness Journey</p>
              <p className="text-sm font-bold text-[var(--text-primary)]">
                {Math.min(100, Math.round((pastClassesMetrics.totalPastClasses / 50) * 100))}%
              </p>
            </div>
            <div className="w-full bg-primary-100 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-primary-500 to-accent-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(100, Math.round((pastClassesMetrics.totalPastClasses / 50) * 100))}%` }}
              />
            </div>
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              {pastClassesMetrics.totalPastClasses < 50 
                ? `${50 - pastClassesMetrics.totalPastClasses} more classes to reach the next milestone!`
                : "You've reached an incredible milestone! Keep up the amazing work!"}
            </p>
          </div>
        </div>
      )}

      {/* Bookings List */}
      {sortedBookings.length === 0 ? (
        <div className="card text-center py-12 hover:shadow-lg transition-all duration-300 border-2 border-transparent hover:border-[var(--border-subtle)]">
          <Calendar className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">No Classes Found</h3>
          <p className="text-[var(--text-secondary)]">
            {filter === 'upcoming'
              ? "You don't have any upcoming classes."
              : "You don't have any past classes."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedBookings.map((booking) => {
            const schedule = booking.schedule;
            const video = schedule?.video || {};
            const dateTimeInfo = getClassDateTime(schedule);
            
            if (!dateTimeInfo) return null;

            const categoryColors = {
              yoga: 'text-purple-700',
              dance: 'text-pink-700',
              martial_arts: 'text-orange-700',
              fitness: 'text-red-700',
              pilates: 'text-blue-700',
              meditation: 'text-indigo-700',
              other: 'text-gray-700'
            };
            const categoryLabels = {
              yoga: 'Yoga',
              dance: 'Dance',
              martial_arts: 'Martial Arts',
              fitness: 'Fitness',
              pilates: 'Pilates',
              meditation: 'Meditation',
              other: 'Other'
            };

            return (
              <div
                key={booking.id}
                className={`card relative group transition-all hover:shadow-xl transform hover:-translate-y-1 border-2 border-transparent hover:border-[var(--border-subtle)] ${
                  dateTimeInfo.isPast ? 'opacity-75' : ''
                }`}
              >
                {/* Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-t-lg overflow-hidden mb-4">
                  {thumbnailsMap[schedule?.videoId] ? (
                    <img
                      src={thumbnailsMap[schedule?.videoId]}
                      alt={video.title || 'Class thumbnail'}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-16 h-16 text-[var(--text-muted)]" />
                    </div>
                  )}
                  {video.category && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-md" style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)' }}>
                      {categoryLabels[video.category] || 'Class'}
                    </div>
                  )}
                  {dateTimeInfo.isRecurring && (
                    <div className="absolute top-3 right-3 bg-blue-500 text-white text-xs font-semibold px-2.5 py-1 rounded-lg shadow-md">
                      Weekly
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2 line-clamp-2 group-hover:text-[var(--text-secondary)] transition-colors">
                    {video.title || 'Unknown Class'}
                  </h3>

                  {/* Date and Time */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                      <Calendar className="w-4 h-4 text-[var(--text-secondary)]" />
                      <span className="text-sm font-medium">{dateTimeInfo.formattedDate}</span>
                      {dateTimeInfo.isToday && (
                        <span className="bg-green-500 text-white text-xs font-semibold px-2 py-0.5 rounded">
                          Today
                        </span>
                      )}
                      {dateTimeInfo.isPast && !dateTimeInfo.isToday && (
                        <span className="bg-gray-500 text-white text-xs font-semibold px-2 py-0.5 rounded">
                          Past
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                      <Clock className="w-4 h-4 text-[var(--text-secondary)]" />
                      <span className="text-sm font-medium">{dateTimeInfo.formattedTime}</span>
                    </div>
                    {schedule.location && (
                      <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                        <MapPin className="w-4 h-4 text-[var(--text-secondary)]" />
                        <span className="text-sm">{schedule.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Duration */}
                  {video.duration && (
                    <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-4">
                      <Video className="w-4 h-4" />
                      <span>
                        {(() => {
                          const durationSeconds = video.duration || 0;
                          const minutes = Math.floor(durationSeconds / 60);
                          const seconds = durationSeconds % 60;
                          if (minutes > 0 && seconds > 0) {
                            return `${minutes} min ${seconds} sec`;
                          } else if (minutes > 0) {
                            return `${minutes} min`;
                          }
                          return `${seconds} sec`;
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Description */}
                  {video.description && (
                    <p className="text-sm text-[var(--text-secondary)] mb-4 line-clamp-2">
                      {video.description}
                    </p>
                  )}

                  {/* Instructor */}
                  {video.instructor && (
                    <div className="flex items-center gap-2 mb-4 p-2 bg-primary-50 rounded-lg">
                      {video.instructor.photoUrl ? (
                        <img
                          src={video.instructor.photoUrl}
                          alt={`${video.instructor.firstName} ${video.instructor.lastName}`}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center">
                          <Users className="w-4 h-4 text-[var(--text-secondary)]" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-[var(--text-primary)]">
                          {video.instructor.firstName} {video.instructor.lastName}
                        </p>
                        <p className="text-xs text-[var(--text-secondary)]">Instructor</p>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    {schedule.specificDate && (
                      <button
                        onClick={() => generateCalendarFileForBooking(booking)}
                        className="btn btn-secondary w-full flex items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <Download className="w-4 h-4" />
                        Export to Calendar
                      </button>
                    )}
                    {dateTimeInfo.isUpcoming && !dateTimeInfo.isRecurring && (
                      <button
                        onClick={() => handleCancelBooking(booking.id)}
                        disabled={cancelBookingMutation.isPending}
                        className="btn btn-outline w-full text-red-600 hover:bg-red-50 border-red-200 flex items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all duration-300"
                      >
                        <XCircle className="w-4 h-4" />
                        Cancel Booking
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

